export interface Project {
  id: string;
  name: string | null;
  verticalId: string | null;
  answers: Record<string, unknown>;
  sizeSf: number | null;
  sizeTranche: string | null;
  constructionSystem: string | null;
  styleSeed: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/** A row in the dashboard list. Deliberately lighter than `Project`. */
export interface ProjectSummary {
  id: string;
  name: string | null;
  verticalId: string | null;
  sizeSf: number | null;
  constructionSystem: string | null;
  status: string;
  answeredCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface StyleImage {
  id: string;
  url: string;
  caption: string | null;
  credit: string;
  width: number | null;
  height: number | null;
}

export interface WeightedValue {
  value: string;
  label: string;
  weight: number;
}

export interface Conflict {
  axis: string;
  axisLabel: string;
  options: WeightedValue[];
  message: string;
}

export interface StyleDNA {
  categorical: Record<string, WeightedValue[]>;
  numeric: Record<string, { mean: number; spread: number }>;
  conflicts: Conflict[];
  imageIds: string[];
}

export interface DnaResponse {
  dna: StyleDNA | null;
  brief: string | null;
  /** The prose describes one coherent cluster, not every pick. */
  briefIsPartial: boolean;
  briefCoveredPicks: number;
  resolutions: Record<string, string>;
  picks: { id: string; url: string; caption: string | null }[];
}

export interface Concept {
  id: string;
  url: string;
  model?: string;
  seed?: number;
  createdAt?: string;
}

export interface ConceptsResponse {
  concepts: Concept[];
  batchesUsedToday: number;
  batchesPerDay: number;
  generationEnabled: boolean;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  org: string | null;
  role: 'customer' | 'admin';
}

export interface AccessRequest {
  id: string;
  name: string;
  email: string;
  org: string | null;
  phone: string | null;
  vertical: string | null;
  message: string | null;
  status: string;
  created_at: string;
  decline_reason: string | null;
}

export interface Band {
  low: number;
  high: number;
}

export interface PlanResponse {
  sizeSf: number;
  stories: number;
  verticalLabel: string;
  systemLabel: string | null;
  site: {
    footprint: Band; stalls: Band; parkingArea: Band; acres: Band;
    parkingBasis: string; fitWarning?: string; notes: string[];
  } | null;
  occupancy: {
    occupantLoad: Band; minimumExits: number; waterClosets: Band; lavatories: Band;
    occupancyGroup: string; loadFactorNote: string; sprinklerLikely: boolean; notes: string[];
  } | null;
  budget: {
    perSf: Band; hard: Band; soft: Band; total: Band;
    systemAdjustment: string | null; notes: string[];
  } | null;
  electrical: {
    connectedLoadKw: Band; demandLoadKw: Band; serviceAmps: number; serviceDescription: string;
    interiorFixtures: Band; exteriorFixtures: Band; lightingPowerBudgetKw: Band;
    evReady: string | null; solarReady: string | null; generator: string | null; notes: string[];
  } | null;
  phasing: { phaseOne: string[]; later: string[]; stubNow: string[]; note: string } | null;
}

export interface ShareLink {
  token: string;
  url: string;
  expiresAt: string | null;
  allowVotes: boolean;
  revoked: boolean;
  views: number;
  createdAt: string;
}

export interface ShareAdminView {
  links: ShareLink[];
  tally: { target: string; average: number; votes: number }[];
  voters: { name: string; email: string | null; votes: number }[];
  comments: { name: string; target: string; score: number; comment: string; at: string }[];
}

export interface SharePublicView {
  projectName: string | null;
  verticalLabel: string | null;
  sizeSf: number | null;
  brief: string | null;
  allowVotes: boolean;
  picks: { id: string; url: string; caption: string | null }[];
  concepts: { id: string; url: string }[];
}

export interface ProjectPatch {
  name?: string | null;
  answers?: Record<string, unknown>;
  sizeSf?: number | null;
  sizeTranche?: string | null;
  constructionSystem?: string | null;
  status?: string;
}

export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: { 'content-type': 'application/json', ...init?.headers },
    });
  } catch {
    throw new ApiError('We could not reach the server. Check your connection.', 0);
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(body?.error ?? 'Something went wrong.', res.status);
  }
  return (await res.json()) as T;
}

export const api = {
  listProjects: () => request<{ projects: ProjectSummary[] }>('/api/projects'),

  createProject: (verticalId: string) =>
    request<Project>('/api/projects', {
      method: 'POST',
      body: JSON.stringify({ verticalId }),
    }),

  getProject: (id: string) => request<Project>(`/api/projects/${id}`),

  updateProject: (id: string, patch: ProjectPatch) =>
    request<Project>(`/api/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  getStyles: (id: string) =>
    request<{ images: StyleImage[]; selected: string[] }>(`/api/projects/${id}/styles`),

  saveSelections: (id: string, imageIds: string[]) =>
    request<{ selected: string[] }>(`/api/projects/${id}/selections`, {
      method: 'PUT',
      body: JSON.stringify({ imageIds }),
    }),

  getDna: (id: string) => request<DnaResponse>(`/api/projects/${id}/dna`),

  computeDna: (id: string, resolutions: Record<string, string> = {}) =>
    request<DnaResponse>(`/api/projects/${id}/dna`, {
      method: 'POST',
      body: JSON.stringify({ resolutions }),
    }),

  getShare: (id: string) => request<ShareAdminView>(`/api/projects/${id}/share`),

  createShare: (id: string, expiresInDays: number | null) =>
    request<{ token: string; url: string; expiresAt: string | null }>(
      `/api/projects/${id}/share`,
      { method: 'POST', body: JSON.stringify({ expiresInDays }) },
    ),

  updateShare: (id: string, token: string, patch: { allowVotes?: boolean; revoked?: boolean }) =>
    request<{ ok: true }>(`/api/projects/${id}/share/${token}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  getSharedPage: (token: string) => request<SharePublicView>(`/api/share/${token}`),

  submitVote: (
    token: string,
    body: { name: string; email?: string; scores: Record<string, number>; comment?: string },
  ) => request<{ ok: true; counted: number }>(`/api/share/${token}/vote`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  getPlan: (id: string) => request<PlanResponse>(`/api/projects/${id}/plan`),

  requestQuote: (id: string, note?: string) =>
    request<{ ok: true; delivered: boolean }>(`/api/projects/${id}/quote`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  getConcepts: (id: string) => request<ConceptsResponse>(`/api/projects/${id}/concepts`),

  me: () => request<{ user: SessionUser | null }>('/api/auth/me'),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  requestAccess: (body: Record<string, string | undefined>) =>
    request<{ ok: true; notified: boolean }>('/api/access/request', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  requestMagicLink: (email: string) =>
    request<{ ok: true }>('/api/auth/magic', { method: 'POST', body: JSON.stringify({ email }) }),

  verifyMagicLink: (token: string) =>
    request<{ user: SessionUser }>('/api/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  adminRequests: (status: string) =>
    request<{ requests: AccessRequest[] }>(`/api/admin/requests?status=${encodeURIComponent(status)}`),

  approveRequest: (id: string) =>
    request<{ ok: true; userId: string; invited: boolean; inviteUrl?: string }>(
      `/api/admin/requests/${id}/approve`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  declineRequest: (id: string, reason?: string) =>
    request<{ ok: true }>(`/api/admin/requests/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  renderConcepts: (id: string, nudge?: string) =>
    request<{ concepts: Concept[]; prompt: string; partial: boolean; estimatedCostCents: number }>(
      `/api/projects/${id}/render`,
      { method: 'POST', body: JSON.stringify(nudge ? { nudge } : {}) },
    ),
};
